import { Injectable } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { Observable, of } from 'rxjs';
import { catchError, map, take } from 'rxjs/operators';
import { RestService } from '../../../../shared/services/rest.service';
import { ToastService } from '../../../../shared/services/toast.service';
import { DueTreatmentModel } from '../../models/medical/due-treatment.model';

@Injectable({ providedIn: 'root' })
export class DueTreatmentRepository {
  constructor(
    private readonly _restService: RestService,
    private readonly _toast: ToastService,
    private readonly _translateService: TranslocoService
  ) {}

  /**
   * Alle Fälligkeiten eines Tiers: `from`/`to` weit gefasst, damit auch Überfälliges kommt,
   * und `onlyInStock=false`, damit auch abgegebene Tiere ihre Liste sehen.
   */
  getByAnimal(animalId: number): Observable<DueTreatmentModel[]> {
    return this.load(`animal=${animalId}&from=2000-01-01&to=2100-12-31&onlyInStock=false`);
  }

  /**
   * Fälligkeiten aller Tiere eines Tierheims im Zeitfenster `from`–`to`.
   * `onlyInStock` ist serverseitig `true`, blendet also abgegebene Tiere aus.
   */
  getByShelter(
    shelterId: number,
    options: { from: string; to: string; limit?: number; status?: string; onlyInStock?: boolean }
  ): Observable<DueTreatmentModel[]> {
    const params = [
      `shelter=${encodeURIComponent(String(shelterId))}`,
      `from=${encodeURIComponent(options.from)}`,
      `to=${encodeURIComponent(options.to)}`,
    ];
    if (options.status) params.push(`status=${encodeURIComponent(options.status)}`);
    if (options.limit) params.push(`limit=${encodeURIComponent(String(options.limit))}`);
    if (options.onlyInStock === false) params.push('onlyInStock=false');

    return this.load(params.join('&'));
  }

  private load(query: string): Observable<DueTreatmentModel[]> {
    return this._restService.httpGet(`/medical/due_treatments?${query}`).pipe(
      take(1),
      map(response => (Array.isArray(response) ? response : [])),
      catchError(() => {
        this._toast.showError(this._translateService.translate('animalMedical.toast.loadError'));
        return of([] as DueTreatmentModel[]);
      })
    );
  }
}
